/* eslint-disable no-unused-vars */
import './style.scss';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getAllWithFilters } from 'apis/proposals';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { NON_PRINTABLE_REGEX } from 'constant';
import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useParams, Link } from 'react-router-dom';
import { MultiSelect } from 'primereact/multiselect';
import Pagination from 'components/Pagination';
import { getAcadamicLevelShortHand } from 'utils/func';

export default function ChooseProposalDialog({
  visible,
  setVisible,
  handleProposalChange,
  proposalId,
  setIsProposalChange,
}) {
  const { t } = useTranslation();
  const { id: projectProposalId } = useParams();
  // #region Data
  const [searchProposalName, setSearchProposalName] = useState('');
  const [disabled, setDisabled] = useState(true);
  const [proposal, setproposal] = useState(null);
  const [pagination, setPagination] = useState({
    first: 0,
    rowsPerPage: 10,
  });

  const [filtersTable, setFiltersTable] = useState({
    _vietnameseName: { value: null },
    _proposalType: { value: null },
    _ownerName: { value: null },
    _program: { value: null },
    _yearProposed: { value: null },
    _status: { value: null },
  });

  // background filters, sync with localStorage
  const [filters, setFilters] = useState({
    vietnameseName: null,
    proposalType: null,
    ownerName: null,
    programId: null,
    yearProposed: null,
    status: ['approved'],
  });

  // applied filters, change every time click on Apply and Clear button for update
  const [appliedFilters, setAppliedFilters] = useState({
    vietnameseName: null,
    proposalType: null,
    ownerName: null,
    programId: null,
    yearProposed: null,
    status: ['approved'],
  });

  const { data: _proposalData, isLoading } = useQuery(
    ['projectProposalList', pagination, projectProposalId, appliedFilters],
    () => getAllWithFilters({ projectProposalId, ...appliedFilters, ...pagination })
  );

  const proposals = useMemo(
    () => _proposalData?.data ?? { data: [], pagination: { row: 10, totalRecords: 0 } },
    [_proposalData]
  );

  const typeProposalOptions = [
    { name: t('proposal.typeProposal.project'), value: 'project' },
    { name: t('proposal.typeProposal.topic'), value: 'topic' },
    { name: t('proposal.typeProposal.productionProject'), value: 'production_project' },
  ];

  const rowClassName = (value) => (value?.deletedAt !== null ? 'p-disabled' : '');

  const filterClear = (field, data) => {
    if (field === 'vietnameseName') {
      setSearchProposalName('');
    }
    const newFilters = { ...filters, [field]: data };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  const vietnameseNameFilter = () => (
    <InputText
      value={filters.vietnameseName}
      onChange={(e) => {
        const newFilters = { ...filters, vietnameseName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.chooseProposal.name')}
      className="p-column-filter"
    />
  );

  const proposalTypeFilter = () => (
    <MultiSelect
      value={filters.proposalType}
      onChange={(e) => {
        const newFilters = { ...filters, proposalType: e.value };
        setFilters(newFilters);
      }}
      options={typeProposalOptions}
      optionLabel="name"
      optionValue="value"
      filter
      showClear
      placeholder={t('projectProposal.chooseProposal.proposalType')}
      maxSelectedLabels={3}
      style={{ maxWidth: '22rem', width: '18.5rem' }}
    />
  );

  const ownerFilter = () => (
    <InputText
      value={filters.ownerName}
      onChange={(e) => {
        const newFilters = { ...filters, ownerName: e.target.value };
        setFilters(newFilters);
      }}
      placeholder={t('projectProposal.chooseProposal.owner')}
      className="p-column-filter"
    />
  );

  const filterActiveStyle = (field) =>
    filters[field] && (filters[field]?.length > 0 || Object.values(filters[field]).some((e) => e))
      ? { color: '#22C55E' }
      : { color: '#6c757d' };

  const filterClearTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.clear')}
      onClick={() => {
        options.filterClearCallback();
        setPagination({ first: 0, rowsPerPage: 10 });
      }}
      severity="secondary"
      outlined
    />
  );

  const filterApplyTemplate = (options) => (
    <Button
      type="button"
      label={t('employee.viewList.apply')}
      onClick={() => {
        options.filterApplyCallback();
      }}
      severity="success"
    />
  );

  const renderHeader = () => (
    <div className="relative p-fluid flex align-items-end">
      <div className="w-10">
        <label className="mb-2">{t('projectProposal.chooseProposal.searchProposalName')}</label>
        <span className="p-input-icon-right">
          <i
            className="pi pi-times hover:surface-200 p-1 border-circle cursor-pointer"
            onClick={() => {
              setSearchProposalName('');
              const newFilters = { ...filters, vietnameseName: null };
              setFilters(newFilters);
              setAppliedFilters(newFilters);
            }}
          />
          <InputText
            className="mt-2"
            placeholder={t('projectProposal.chooseProposal.placeholderSearchProposalName')}
            onChange={(e) => {
              const formattedValue = e.target.value?.replace(NON_PRINTABLE_REGEX, '');
              setSearchProposalName(formattedValue);
            }}
            value={searchProposalName}
          />
        </span>
      </div>
      <div className="ml-4">
        <Button
          label={t('projectProposal.chooseProposal.searchButton')}
          severity="info"
          type="button"
          onClick={() => {
            const newFilters = {
              ...filters,
              vietnameseName: searchProposalName,
              first: 0,
              rowsPerPage: 10,
            };
            setPagination({ first: 0, rowsPerPage: 10 });
            setFilters(newFilters);
            setAppliedFilters(newFilters);
          }}
          className="mr-2 w-8rem"
        />
      </div>
    </div>
  );

  const formatName = (value) => (
    // <Link to={`/proposal/view/${value.id}`} target="_blank">
    <span>{value?.vietnameseName}</span>
    // </Link>
  );

  const formatOwnerName = ({ owner }) => (
    // <Link to={`/proposal/view/${value.id}`} target="_blank">
    <span>
      {getAcadamicLevelShortHand(
        owner.scientificProfile?.academicRank,
        owner.scientificProfile?.academicDegree,
        t,
        true
      )}{' '}
      {owner.fullname}
    </span>
    // </Link>
  );

  const formatProposalType = (value) =>
    typeProposalOptions.map(
      (type) =>
        type.value === value.type && (
          <p key={type.value} className="m-0">
            {type.name}
          </p>
        )
    );

  const formatResearchField = (value) =>
    value.proposalResearchFields.map((researchField) => (
      <p key={researchField.researchField.code} className="m-0 ">
        {t(`researchFields.${researchField.researchField.code}`)}
      </p>
    ));

  // const formatActions = (value) => (
  //   <div className="flex flex-wrap justify-content-center gap-1">
  //     <div className="flex align-items-center flex-wrap bg-blue-500 border-round-lg">
  //       <Link to={`/proposal/view/${value.id}?from=project-proposal`} target="_blank">
  //         <Button
  //           // eslint-disable-next-line react/jsx-indent-props
  //           icon="pi pi-eye"
  //           severity="info"
  //           tooltip={t('proposal.view.label')}
  //           tooltipOptions={{ position: 'left', at: 'left top' }}
  //           style={{ width: '42px', height: '42px' }}
  //         />
  //       </Link>
  //     </div>
  //   </div>
  // );

  // #endregion

  // #region Renders

  const renderFields = [
    {
      field: 'vietnameseName',
      body: formatName,
      header: t('projectProposal.chooseProposal.name'),
      style: { maxWidth: '20rem' },
      filter: true,
      filterField: '_vietnameseName',
      filterElement: vietnameseNameFilter,
      onFilterClear: () => filterClear('vietnameseName', null),
      frozen: true,
    },
    {
      field: 'proposalType',
      body: formatProposalType,
      header: t('projectProposal.chooseProposal.proposalType'),
      style: { maxWidth: '6rem', textAlign: 'center' },
      filterField: '_proposalType',
      filter: true,
      filterElement: proposalTypeFilter,
      onFilterClear: () => filterClear('proposalType', null),
    },
    {
      body: formatResearchField,
      header: t('projectProposal.chooseProposal.researchField'),
      style: { maxWidth: '10rem' },
      filterField: '_researchField',
    },
    {
      field: 'owner.fullname',
      header: t('projectProposal.chooseProposal.owner'),
      headerStyle: 'ownerName',
      body: formatOwnerName,
      style: { maxWidth: '12rem', textAlign: 'left' },
      filter: true,
      filterField: '_ownerName',
      filterElement: ownerFilter,
      onFilterClear: () => filterClear('ownerName', null),
    },
    // {
    //   body: formatActions,
    //   header: t('projectProposal.chooseProposal.action'),
    //   style: { maxWidth: '3rem', textAlign: 'center' },
    // },
  ];
  // #endregion

  // #region Events

  useEffect(() => {
    if (proposalId && proposals?.data) {
      const selectedProposal = proposals?.data?.find((item) => item.id === proposalId);
      setproposal(selectedProposal);
    }
  }, [proposalId, proposals?.data]);

  // #endregion
  return (
    <Dialog
      header={<h3 className="mb-0">{t('projectProposal.chooseProposal.title')}</h3>}
      visible={visible}
      position="center"
      style={{ width: '90%', maxWidth: '1200px' }}
      onHide={() => {
        setVisible(false);
        setDisabled(true);
      }}
      draggable={false}
    >
      <DataTable
        value={proposals?.data || []}
        className="proposal-dialog-list"
        dataKey="id"
        header={renderHeader}
        showGridlines
        scrollable
        selection={proposal}
        onSelectionChange={(e) => {
          setproposal(e.value);
          if (disabled) {
            setDisabled(false);
          }
        }}
        rowClassName={rowClassName}
        filters={filtersTable}
        onFilter={(e) => setFiltersTable(e.filters)}
        filterDisplay="menu"
        loading={isLoading}
        emptyMessage={t('projectProposal.chooseProposal.emptyMessage')}
      >
        <Column selectionMode="single" headerStyle={{ width: '3rem' }} frozen alignFrozen="left" />
        {renderFields?.map((field) => (
          <Column
            key={field.field}
            field={field?.field || ''}
            header={field?.header || ''}
            body={field?.body}
            headerStyle={filterActiveStyle(field?.headerStyle || field.field)}
            filter={field?.filter || false}
            filterField={field?.filterField || ''}
            filterElement={field?.filterElement || ''}
            filterClear={filterClearTemplate}
            filterApply={filterApplyTemplate}
            showFilterMenu
            showFilterMatchModes={false}
            onFilterClear={field?.onFilterClear || ''}
            onFilterApplyClick={() => {
              setAppliedFilters(filters);
              if (filters?.vietnameseName !== searchProposalName) {
                setSearchProposalName(filters?.vietnameseName);
              }
            }}
            style={field?.style || {}}
            frozen={field?.frozen || false}
            alignFrozen={field.alignFrozen || 'left'}
          />
        ))}
      </DataTable>

      <Pagination
        pagination={pagination}
        setPagination={setPagination}
        paginationData={proposals?.pagination}
        pageName="choose-proposal-dialog"
      />
      <div className="flex flex-wrap justify-content-end mt-4">
        <Button
          label={proposalId === '' ? t('formLayout.action.choose') : t('formLayout.action.update')}
          severity="info"
          type="submit"
          className="w-8rem"
          onClick={() => {
            handleProposalChange(proposal?.vietnameseName, proposal?.id || null);
            setVisible(false);
            setDisabled(true);
            if (proposalId !== proposal?.id) {
              setIsProposalChange(true);
            }
          }}
          // disabled={proposal === null || disabled}
          disabled={disabled}
        />
      </div>
    </Dialog>
  );
}

ChooseProposalDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  handleProposalChange: PropTypes.func.isRequired,
  proposalId: PropTypes.string,
  setIsProposalChange: PropTypes.func,
};

ChooseProposalDialog.defaultProps = {
  proposalId: '',
  setIsProposalChange: () => {},
};
